import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "../css/style.css";
import logo from "../assets/image/rccglogo.webp";

export default function ResetPassword() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    const storedEmail = localStorage.getItem("resetEmail");
    if (storedEmail) {
      setEmail(storedEmail);
    } else {
      navigate("/forgot-password", { replace: true });
    }

    return () => {
      isMounted.current = false;
    };
  }, [navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setTimeout(() => {
      setResendCooldown(resendCooldown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleChange = (e, index) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (!value) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];
      if (!otp[index] && index > 0) inputRefs.current[index - 1]?.focus();
      newOtp[index] = "";
      setOtp(newOtp);
    }
  };

  const handleVerifyOtp = async () => {
    if (loading) return;

    const code = otp.join("");
    if (code.length !== 6) {
      toast.error("Please enter the full 6-digit OTP.", { theme: "colored" });
      return;
    }

    setLoading(true);
    try {
      const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;
      const res = await fetch(`${TEST_BASE_URL}/auth/verify-reset-password-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp: code }),
      });

      const data = await res.json();

      if (!res.ok) {
        const msg = data.message?.toLowerCase().includes("invalid")
          ? "Invalid OTP. Please check and try again."
          : data.message || "OTP verification failed.";
        toast.error(msg, { theme: "colored" });
        if (isMounted.current) setLoading(false);
        return;
      }

      localStorage.setItem("verifiedEmail", email);
      localStorage.setItem("verifiedOtp", code);

      toast.success("OTP verified successfully!", { theme: "colored" });

      setTimeout(() => {
        if (isMounted.current) navigate("/new-password", { replace: true });
      }, 1500);
    } catch (err) {
      console.error(err);
      toast.error("Network error — please try again.", { theme: "colored" });
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendLoading || resendCooldown > 0) return;

    setResendLoading(true);
    try {
      const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;
      const res = await fetch(`${TEST_BASE_URL}/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.message || "Failed to resend OTP.", { theme: "colored" });
        if (isMounted.current) setResendLoading(false);
        return;
      }

      toast.success("OTP resent successfully! Check your email.", { theme: "colored" });
      
      setOtp(["", "", "", "", "", ""]);
      
      inputRefs.current[0]?.focus();
      
      if (isMounted.current) {
        setResendCooldown(30);
      }
    } catch (err) {
      console.error(err);
      toast.error("Network error — please try again.", { theme: "colored" });
    } finally {
      if (isMounted.current) setResendLoading(false);
    }
  };

  return (
    <>
      <section className="login">
        <div className="containerfluid">
          <div className="row loginrow">
            <div className="col-md-6">
              <div className="loginbg">
                <img src={logo} alt="RCCG Logo" />
                <h5 className="py-5">Welcome to RCCG New Beginning Chapel, Bournemouth, UK</h5>
                <p className="text-white">
                  RCCG Bournemouth Membership Portal has been built to create a
                  community where the church members can interact with each
                  other seamlessly.
                </p>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card resetcard">
                <h5>Reset Password</h5>
                <p>
                  We have sent you a code at <b>{email || "your email address"}</b>
                </p>

                <div className="otp-inputs">
                  {otp.map((_, i) => (
                    <input
                      key={i}
                      type="tel"
                      maxLength="1"
                      ref={(el) => (inputRefs.current[i] = el)}
                      value={otp[i]}
                      onChange={(e) => handleChange(e, i)}
                      onKeyDown={(e) => handleKeyDown(e, i)}
                      className="otp-box"
                      disabled={loading}
                    />
                  ))}
                </div>

                <p>
                  Didn't receive a code?{" "}
                  <button
                    onClick={handleResendOtp}
                    disabled={resendLoading || resendCooldown > 0}
                    className="text-primary"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: resendCooldown > 0 ? "not-allowed" : "pointer",
                      color: resendCooldown > 0 ? "#999" : "#0045E8",
                      textDecoration: "underline",
                      padding: 0,
                      font: "inherit",
                    }}
                  >
                    <u>
                      {resendLoading
                        ? "Sending..."
                        : resendCooldown > 0
                        ? `Resend in ${resendCooldown}s`
                        : "Click to resend"}
                    </u>
                  </button>
                </p>

                <button
                  className="btn verify-btn mt-3"
                  onClick={handleVerifyOtp}
                  disabled={loading}
                >
                  {loading ? "Verifying..." : "Verify OTP"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <ToastContainer position="top-right" autoClose={1500} />
    </>
  );
}